import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, ArrowLeft, Mail, Languages, Loader2 } from "lucide-react";
import { LOGO_URL } from "../lib/brand";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useT } from "../i18n/LanguageContext";
import { usePageSEO } from "../hooks/usePageSEO";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const formatDate = (iso, locale) => {
  try {
    return new Date(iso).toLocaleDateString(locale === "en" ? "en-GB" : "de-CH", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const resolveCover = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
};

export default function BlogPost() {
  const { slug } = useParams();
  const { lang: siteLang, t } = useT();
  const tb = t?.blog || {};
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Per-page language is driven by the site-wide switcher. A local override
  // is no longer needed — the LanguageSwitcher in the header already toggles
  // it. We keep `lang` as a derived value so the post automatically re-renders
  // when the visitor flips the global switcher.
  const lang = siteLang === "en" ? "en" : "de";
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
        if (res.data?.en_translation) setTranslation(res.data.en_translation);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Lazy-load translation when the site is set to English and we haven't
  // fetched/cached it yet. Stays idempotent — the backend caches per slug.
  useEffect(() => {
    if (lang !== "en" || translation || !post) return;
    let cancelled = false;
    (async () => {
      setTranslating(true);
      try {
        const res = await axios.get(`${API}/blog/${slug}/translation`);
        if (!cancelled) setTranslation(res.data);
      } catch {
        if (!cancelled) toast.error("Translation failed. Please try again later.");
      } finally {
        if (!cancelled) setTranslating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang, slug, post, translation]);

  const display = lang === "en" && translation
    ? { title: translation.title, excerpt: translation.excerpt, content_html: translation.content_html }
    : post
      ? { title: post.title, excerpt: post.excerpt, content_html: post.content_html }
      : null;

  usePageSEO({
    title: display ? `${display.title} | Alphalane Blog` : (lang === "en" ? "Loading…" : "Beitrag…"),
    description: display?.excerpt || (lang === "en" ? "Loading article." : "Beitrag wird geladen."),
    canonical: `https://alphalane-tradingsystems.com/blog/${slug || ""}`,
  });

  // JSON-LD
  useEffect(() => {
    if (!post) return;
    const id = "post-jsonld";
    const old = document.getElementById(id);
    if (old) old.remove();
    const data = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": resolveCover(post.cover),
      "datePublished": post.date,
      "author": { "@type": "Organization", "name": "Alphalane Trading Systems" },
      "publisher": {
        "@type": "Organization",
        "name": "Alphalane Trading Systems",
        "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_web-design-builder-3/artifacts/bmldq7j5_Logo.PNG" },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://alphalane-tradingsystems.com/blog/${post.slug}` },
      "keywords": (post.keywords || []).join(", "),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => script.remove();
  }, [post]);

  const toggleLanguage = null;
  void toggleLanguage; // intentionally unused — kept for potential future inline toggle

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !post || !display) {
    return (
      <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> {lang === "en" ? "Loading article…" : "Lade Artikel…"}
      </div>
    );
  }

  const cover = resolveCover(post.cover);

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <span className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">{tb.tag || "Blog"}</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/blog" className="al-cta-ghost !py-2 !px-4 text-sm">
              <ArrowLeft className="h-4 w-4" /> {tb.back || "Blog"}
            </Link>
          </div>
        </div>
      </header>

      {cover && (
        <div className="relative h-[280px] sm:h-[400px] overflow-hidden border-b border-[var(--al-line)]">
          <img src={cover} alt={display.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--al-bg)] via-[var(--al-bg)]/60 to-transparent" />
        </div>
      )}

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 text-xs uppercase tracking-widest font-semibold text-slate-500">
          <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(post.date, lang)}</span>
          <span>·</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.read_time}</span>
          {translating && (
            <>
              <span>·</span>
              <span className="flex items-center gap-1.5 text-[var(--al-purple)]" data-testid="blog-translating">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {lang === "en" ? "Translating…" : "Übersetze…"}
              </span>
            </>
          )}
        </div>
        <h1 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">{display.title}</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">{display.excerpt}</p>

        <div className="al-divider my-10" />

        <article className="post-body" dangerouslySetInnerHTML={{ __html: display.content_html }} />

        <div className="mt-14 al-card p-7 sm:p-9 al-grad-soft-bg">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">
            {lang === "en" ? "Ready for the next step?" : "Bereit für den nächsten Schritt?"}
          </div>
          <h3 className="mt-2 font-display font-semibold text-2xl">
            {lang === "en" ? "Let your capital work for you." : "Lass dein Kapital für dich arbeiten."}
          </h3>
          <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">
            {lang === "en"
              ? "Send us a non-binding request — we'll show you how our AI trading system works for you."
              : "Stell uns unverbindlich eine Anfrage — wir zeigen dir, wie unser KI-Trading-System für dich arbeitet."}
          </p>
          <Link to="/#kontakt" className="mt-5 al-cta inline-flex">
            <Mail className="h-4 w-4" /> {lang === "en" ? "Send a request" : "Anfrage stellen"}
          </Link>
        </div>
      </main>

      <footer className="border-t border-[var(--al-line)] bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Alphalane Trading Systems</div>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-slate-900">{lang === "en" ? "Imprint" : "Impressum"}</Link>
            <Link to="/datenschutz" className="hover:text-slate-900">{lang === "en" ? "Privacy" : "Datenschutz"}</Link>
            <Link to="/agb" className="hover:text-slate-900">{lang === "en" ? "Terms" : "AGB"}</Link>
          </div>
        </div>
      </footer>

      <style>{`
        .post-body p { color: var(--al-ink-soft); line-height: 1.8; margin: 0 0 1.1rem; font-size: 16px; }
        .post-body h2 { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1.6rem; line-height: 1.2; letter-spacing: -0.02em; color: var(--al-ink); margin: 2.5rem 0 1rem; }
        .post-body h3 { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 1.2rem; color: var(--al-ink); margin: 2rem 0 0.75rem; }
        .post-body strong { color: var(--al-ink); font-weight: 600; }
        .post-body em { color: var(--al-ink-soft); }
        .post-body ul { color: var(--al-ink-soft); line-height: 1.8; margin: 0.5rem 0 1.25rem 1.25rem; list-style: disc; }
        .post-body ul li { margin-bottom: 0.4rem; }
        .post-body a { color: var(--al-purple); text-decoration: underline; text-underline-offset: 3px; }
        .post-body a:hover { color: var(--al-deep); }
      `}</style>
    </div>
  );
}
